import { createContext, useContext, useEffect, useState, useCallback } from "react";

/* Light/Dark theme context.

   Defaults to "dark" (matches the original Warm Editorial Dark design) so
   existing users see no change. Toggling adds/removes the `.dark` class on
   <html> — Tailwind is configured with `darkMode: ["class"]`, and our CSS
   variables in index.css are scoped to `:root` (light) vs `.dark` (dark).
*/

const ThemeContext = createContext({
  theme: "dark",
  toggleTheme: () => {},
  setTheme: () => {},
});

const STORAGE_KEY = "estima_theme";

function readInitialTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch { /* localStorage unavailable */ }
  // App is a "Warm Editorial Dark" experience by default. Only honour explicit
  // user preference once stored.
  return "dark";
}

function applyTheme(t) {
  const root = document.documentElement;
  if (t === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  // also expose color-scheme so native widgets (scrollbars, form controls) match
  root.style.colorScheme = t;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  const setTheme = useCallback((t) => {
    if (t === "light" || t === "dark") setThemeState(t);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
